import { useState, useMemo, useEffect, useRef } from 'react';
import { Grid, Chip, Typography, Button } from '@mui/material';
import { isNumber, isFunction, isArray, mean, isEmpty, round, isEqual, min, max } from 'lodash';
import { red, grey, blue } from '@mui/material/colors';

export default function ColorCheckField({ initial, onChange, config, debounceMs = 1000, editable }) {
  const [values, setValues] = useState([]);
  const lastResults = useRef([]);
  const resultTimer = useRef(null);

  // if the ranges are boubd to a field and one of them not provider
  const dataNotProvided =
    !isNumber(config?.acceptableMin) ||
    !isNumber(config?.acceptableMax) ||
    !isNumber(config?.rangeMin) ||
    !isNumber(config?.rangeMax);

  const invalidRange = config?.rangeMin > config?.rangeMax;

  const buttonValues = useMemo(() => {
    if (!isNumber(config?.rangeMin) || !isNumber(config?.rangeMax) || !isNumber(config?.step)) {
      return [];
    }

    if (config.rangeMin > config.rangeMax) {
      return [];
    }

    const result = [];

    // calculate buttons form min to max
    let curr = config.rangeMin;
    while (curr <= config.rangeMax) {
      result.push(curr);
      curr += config.step;
    }

    return result;
  }, [config]);

  const { average, inSpecsRatio, minimum, maximum } = useMemo(() => {
    if (isEmpty(values) || !isNumber(config?.acceptableMin) || !isNumber(config?.acceptableMax)) {
      return { average: null, inSpecsRatio: null, minimum: null, maximum: null };
    }

    const average = round(mean(values), 2);
    const inSpecsRatio = round(
      values.filter((x) => x >= config.acceptableMin && x <= config.acceptableMax).length / values.length,
      2,
    );
    const minimum = min(values);
    const maximum = max(values);

    return {
      average,
      inSpecsRatio,
      minimum,
      maximum,
    };
  }, [values, config]);

  useEffect(() => {
    // mitigate side effect of debouncing
    if (initial?.value && lastResults.current.some((x) => isEqual(x, initial?.value))) {
      return;
    }
    if (!isArray(initial?.value)) {
      setValues([]);
      return;
    }
    setValues(initial.value);
  }, [initial]);

  function saveValues(value) {
    setValues(value);

    if (resultTimer.current != null) {
      clearTimeout(resultTimer.current);
    }

    resultTimer.current = setTimeout(() => sendResult({ value }), debounceMs);
  }

  const sendResult = (res) => {
    if (!isFunction(onChange)) return;

    // store the last 3 sent results
    const resultsToStore = 10;
    lastResults.current.push(res.value);
    lastResults.current = lastResults.current.slice(-resultsToStore);

    onChange(res);
  };

  return (
    <Grid container alignItems={'center'}>
      {dataNotProvided && (
        <Grid container alignItems={'center'}>
          <Typography sx={{ color: red[500], fontStyle: 'italic' }}>Data for colour range not provided</Typography>
        </Grid>
      )}
      {invalidRange && (
        <Grid container alignItems={'center'}>
          <Typography sx={{ color: red[500], fontStyle: 'italic' }}>Invalid range: min more than max</Typography>
        </Grid>
      )}
      {editable && (
        <Grid container alignItems={'center'} sx={{ mt: 1 }} spacing={1}>
          {buttonValues.map((value, index) => (
            <Grid item key={index}>
              <Button
                onClick={() => saveValues([...values, value])}
                variant="outlined"
                sx={{
                  borderRadius: 50,
                  borderWidth: 2,
                  ':hover': { borderWidth: 2 },
                }}
              >
                {value}
              </Button>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container alignItems={'center'} sx={{ mt: 1 }} spacing={1}>
        {values.map((value, index) => (
          <Grid item key={index}>
            <Chip
              label={value}
              color="primary"
              onDelete={editable ? () => saveValues(values.filter((_x, i) => i !== index)) : undefined}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container alignItems={'center'} sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Result:
        </Typography>
      </Grid>

      <Grid container alignItems={'center'} sx={{ mt: 1 }}>
        <Chip sx={{ mr: 1 }} label={`avg: ${average ?? '-'}`} />
        <Chip sx={{ mr: 1 }} label={`min: ${minimum ?? '-'}`} />
        <Chip sx={{ mr: 1 }} label={`max: ${maximum ?? '-'}`} />
        <Chip
          sx={{ background: blue[500] }}
          label={`In specs: ${inSpecsRatio != null ? `${round(inSpecsRatio * 100, 0)}%` : '-'}`}
        />
      </Grid>
    </Grid>
  );
}
